// AgentSession — one end-to-end agent run. Bounded. Always emits ≥1 audit
// event (`session.boot`) or it didn't run.

import { z } from 'zod';
import {
  IssueIdSchema,
  PullRequestIdSchema,
  RepoIdSchema,
  SessionIdSchema,
} from '../value-objects/ids';
import { BYOKProviderSchema } from './byok';

export const SessionTrigger = {
  Issue: 'issue',
  Pr: 'pr',
  Release: 'release',
  Manual: 'manual',
} as const;
export const SessionTriggerSchema = z.enum(SessionTrigger);
export type SessionTrigger = z.infer<typeof SessionTriggerSchema>;

export const SessionOutcome = {
  Completed: 'completed',
  Escalated: 'escalated',
  Failed: 'failed',
  /**
   * The deploy drain CUT this session and its job was handed back for a fresh one
   * (plan 2026-10-01/101 row 02-3): a roll is the platform's event, so neither a
   * verdict on the work (`failed`) nor a finish (`completed`). Never billable
   * (`@developerz/billing` `BILLABLE_OUTCOMES`), and never the model's to choose —
   * `session.end` takes its own three-member enum.
   */
  HandedBack: 'handed_back',
} as const;
export const SessionOutcomeSchema = z.enum(SessionOutcome);
export type SessionOutcome = z.infer<typeof SessionOutcomeSchema>;

export const EntityKind = {
  Issue: 'issue',
  Pr: 'pr',
  Repo: 'repo',
} as const;
export const EntityKindSchema = z.enum(EntityKind);
export type EntityKind = z.infer<typeof EntityKindSchema>;

/** Discriminated entity ref: which thing this session ran against. */
export const SessionEntityRefSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal(EntityKind.Issue), id: IssueIdSchema }),
  z.object({ kind: z.literal(EntityKind.Pr), id: PullRequestIdSchema }),
  z.object({ kind: z.literal(EntityKind.Repo), id: RepoIdSchema }),
]);
export type SessionEntityRef = z.infer<typeof SessionEntityRefSchema>;

export const AgentSessionSchema = z.object({
  id: SessionIdSchema,
  repoId: RepoIdSchema,
  trigger: SessionTriggerSchema,
  entity: SessionEntityRefSchema,
  provider: BYOKProviderSchema,
  model: z.string().min(1),
  startedAt: z.date(),
  finishedAt: z.date().nullable(),
  outcome: SessionOutcomeSchema.nullable(),
  tokenIn: z.number().int().nonnegative(),
  tokenOut: z.number().int().nonnegative(),
  costUsd: z.number().nonnegative(),
  /** Set on replay; references the original session id. */
  replayOf: SessionIdSchema.nullable(),
});
export type AgentSession = z.infer<typeof AgentSessionSchema>;

/**
 * Invariant: a finished session must have an outcome; an unfinished one must not.
 */
export function outcomeMatchesFinished(session: AgentSession): boolean {
  return (session.finishedAt === null) === (session.outcome === null);
}
